// File: frontend/src/components/DiccionarioCpvs.tsx

import React, { useEffect, useState, useCallback } from "react";
import { fetchDiccionarioCpvs } from "../api/diccionarioCpvsApi";
import { useAuth } from "../security/AuthLogin";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSpinner,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "../css/diccionariocpvs.css";

interface CpvItem {
  cod_cpv: string;
  cpv: string;
  cod_clasificacion: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const DiccionarioCpvs: React.FC = () => {
  const { userRole } = useAuth();
  const [cpvs, setCpvs] = useState<CpvItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const loadCpvs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm.trim(),
      };

      // Filtrar parámetros vacíos
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(
          ([_, value]) => value !== "" && value !== null && value !== undefined
        )
      );

      const response = await fetchDiccionarioCpvs(cleanParams);

      if (response.success) {
        setCpvs(response.data);
        setPagination(response.pagination);
      } else {
        setError("Error al cargar los CPVs");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar los CPVs");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  // Efecto para la búsqueda en tiempo real con debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Resetear a la página 1 cuando cambie la búsqueda
      if (pagination.page !== 1) {
        setPagination((prev) => ({ ...prev, page: 1 }));
      } else {
        loadCpvs();
      }
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Efecto para cargar cuando cambie la página o el límite
  useEffect(() => {
    loadCpvs();
  }, [pagination.page, pagination.limit]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // Verificar permisos
  if (userRole !== "admin" && userRole !== "tecnico") {
    return (
      <div className="cpv-dictionary-container">
        <div className="cpv-error-message">
          No tienes permisos para acceder al diccionario de CPVs.
        </div>
      </div>
    );
  }

  return (
    <div className="cpv-dictionary-container">
      {/* Header */}
      <div className="cpv-header">
        <div className="cpv-title-section">
          <h1>Diccionario de CPVs</h1>
          <p>Consulta y busca códigos CPV del sistema</p>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="cpv-search-section">
        <div className="cpv-search-container">
          <div className="cpv-search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="cpv-search-icon" />
            <input
              type="text"
              className="cpv-search-input"
              placeholder="Buscar por código CPV, descripción o clasificación..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className="cpv-search-controls">
            <label htmlFor="limit-select">Mostrar:</label>
            <select
              id="limit-select"
              className="cpv-limit-select"
              value={pagination.limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
            <span>registros</span>
          </div>
        </div>
      </div>

      {/* Información de resultados */}
      <div className="cpv-results-info">
        {loading ? (
          <span className="cpv-loading-text">
            <FontAwesomeIcon icon={faSpinner} spin />
            Cargando...
          </span>
        ) : (
          <span className="cpv-results-text">
            {pagination.total > 0 ? (
              <>
                Mostrando {cpvs.length} de {pagination.total.toLocaleString()}{" "}
                registros
                {searchTerm && (
                  <span className="cpv-search-term"> para "{searchTerm}"</span>
                )}
              </>
            ) : searchTerm ? (
              `No se encontraron resultados para "${searchTerm}"`
            ) : (
              "No hay registros disponibles"
            )}
          </span>
        )}
      </div>

      {/* Error message */}
      {error && <div className="cpv-error-message">{error}</div>}

      {/* Tabla de resultados */}
      {!loading && cpvs.length > 0 && (
        <div className="cpv-table-container">
          <table className="cpv-table">
            <thead>
              <tr>
                <th>Código CPV</th>
                <th>Descripción</th>
                <th>Clasificación</th>
              </tr>
            </thead>
            <tbody>
              {cpvs.map((cpv, index) => (
                <tr key={`${cpv.cod_cpv}-${index}`}>
                  <td className="cpv-code-cell">{cpv.cod_cpv}</td>
                  <td className="cpv-description-cell">{cpv.cpv}</td>
                  <td className="cpv-classification-cell">
                    <span className="cpv-classification-badge">
                      {cpv.cod_clasificacion}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mensaje cuando no hay datos */}
      {!loading && cpvs.length === 0 && !error && (
        <div className="cpv-no-data">
          {searchTerm ? (
            <>
              <FontAwesomeIcon icon={faSearch} />
              <p>No se encontraron CPVs que coincidan con tu búsqueda.</p>
              <p>Intenta con otros términos o revisa la ortografía.</p>
            </>
          ) : (
            <>
              <p>No hay datos disponibles en este momento.</p>
            </>
          )}
        </div>
      )}

      {/* Paginación */}
      {!loading && pagination.totalPages > 1 && (
        <div className="cpv-pagination">
          <button
            className="cpv-pagination-btn cpv-pagination-prev"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={!pagination.hasPrevPage}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            Anterior
          </button>

          <div className="cpv-pagination-info">
            <span>
              Página {pagination.page} de {pagination.totalPages}
            </span>
          </div>

          <button
            className="cpv-pagination-btn cpv-pagination-next"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={!pagination.hasNextPage}
          >
            Siguiente
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
};

export default DiccionarioCpvs;
